'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { getRoleDashboardPath } from '@/lib/roleRouting';
import BrandLogo from '@/components/ui/BrandLogo';

type UserRole = 'tourist' | 'guide' | 'hotel_owner' | 'vehicle_owner' | 'travel_agency';

interface RoleOption {
  value: UserRole;
  label: string;
  icon: string;
  emoji: string;
  description: string;
  gradient: string;
  glow: string;
}

const ROLES: RoleOption[] = [
  {
    value: 'tourist',
    label: 'Tourist',
    icon: '🧳',
    emoji: '🧳',
    description: 'Explore Nepal\'s wonders',
    gradient: 'from-sky-400/20 to-blue-600/20',
    glow: 'rgba(56,189,248,0.3)',
  },
  {
    value: 'guide',
    label: 'Local Guide',
    icon: '🏔️',
    emoji: '🏔️',
    description: 'Lead adventures & treks',
    gradient: 'from-emerald-400/20 to-teal-600/20',
    glow: 'rgba(52,211,153,0.3)',
  },
  {
    value: 'hotel_owner',
    label: 'Hotel Owner',
    icon: '🏨',
    emoji: '🏨',
    description: 'Host & welcome travellers',
    gradient: 'from-yellow-400/20 to-amber-600/20',
    glow: 'rgba(251,191,36,0.3)',
  },
  {
    value: 'vehicle_owner',
    label: 'Vehicle Owner',
    icon: '🚐',
    emoji: '🚐',
    description: 'Provide transport services',
    gradient: 'from-orange-400/20 to-red-600/20',
    glow: 'rgba(251,146,60,0.3)',
  },
  {
    value: 'travel_agency',
    label: 'Travel Agency',
    icon: '✈️',
    emoji: '✈️',
    description: 'Curate premium packages',
    gradient: 'from-emerald-400/20 to-green-600/20',
    glow: 'rgba(52,211,153,0.3)',
  },
];

function getPasswordStrength(pw: string): { score: number; label: string; color: string } {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { score: 1, label: 'Weak', color: '#ef4444' };
  if (score === 2) return { score: 2, label: 'Fair', color: '#f59e0b' };
  if (score === 3) return { score: 3, label: 'Good', color: '#3b82f6' };
  return { score: 4, label: 'Strong', color: '#22c55e' };
}

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidPhone(phone: string) {
  return phone === '' || /^[+]?[\d\s\-()]{7,15}$/.test(phone);
}

interface FloatingInputProps {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  autoComplete?: string;
  error?: string;
  success?: boolean;
  rightSlot?: React.ReactNode;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

function FloatingInput({
  label, type = 'text', value, onChange, required, autoComplete, error, success, rightSlot, inputMode,
}: FloatingInputProps) {
  const [focused, setFocused] = useState(false);
  const lifted = focused || value.length > 0;

  return (
    <div className="relative">
      <div
        className={`relative rounded-2xl transition-all duration-300 ${
          error
            ? 'ring-2 ring-red-400/60'
            : success
            ? 'ring-2 ring-emerald-400/60'
            : focused
            ? 'ring-2 ring-[#F4C542]/60'
            : 'ring-1 ring-white/10'
        }`}
        style={{
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: focused
            ? '0 0 0 4px rgba(244,197,66,0.08), inset 0 1px 0 rgba(255,255,255,0.1)'
            : 'inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        <label
          className="absolute left-4 transition-all duration-200 pointer-events-none select-none font-medium"
          style={{
            top: lifted ? '8px' : '50%',
            transform: lifted ? 'translateY(0) scale(0.75)' : 'translateY(-50%) scale(1)',
            transformOrigin: 'left center',
            color: error ? '#f87171' : success ? '#34d399' : focused ? '#F4C542' : 'rgba(255,255,255,0.45)',
            fontSize: '15px',
            zIndex: 1,
          }}
        >
          {label}{required && ' *'}
        </label>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          autoComplete={autoComplete}
          inputMode={inputMode}
          className="w-full bg-transparent outline-none text-white text-[15px] font-medium"
          style={{
            padding: lifted ? '26px 48px 10px 16px' : '18px 48px 18px 16px',
            paddingRight: rightSlot ? '48px' : '16px',
          }}
          aria-label={label}
        />
        {rightSlot && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">{rightSlot}</div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 ml-1 text-red-400 text-xs flex items-center gap-1">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
          {error}
        </p>
      )}
      {success && !error && (
        <p className="mt-1.5 ml-1 text-emerald-400 text-xs flex items-center gap-1">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" strokeLinecap="round"/></svg>
          Looks good
        </p>
      )}
    </div>
  );
}

export default function RegisterPage() {
  const { signUp } = useAuth();
  const router = useRouter();

  const [step, setStep] = useState<1 | 2>(1);
  const [mounted, setMounted] = useState(false);
  const [animating, setAnimating] = useState(false);

  const [selectedRole, setSelectedRole] = useState<UserRole>('tourist');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [touched, setTouched] = useState({
    fullName: false, email: false, phone: false, password: false, confirmPassword: false,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  const goToStep2 = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setTouched(t => ({ ...t, fullName: true, email: true, phone: true }));
    if (!fullName.trim()) { setError('Please enter your full name.'); return; }
    if (!email.trim() || !isValidEmail(email)) { setError('Please enter a valid email address.'); return; }
    if (phone && !isValidPhone(phone)) { setError('Please enter a valid phone number.'); return; }
    setAnimating(true);
    setTimeout(() => {
      setStep(2);
      setAnimating(false);
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setTouched(t => ({ ...t, password: true, confirmPassword: true }));
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (!agreeTerms) { setError('Please accept the terms and conditions.'); return; }

    setLoading(true);
    try {
      const data = await signUp(email, password, { fullName, role: selectedRole, phone, country: 'Nepal' });
      setSuccess(true);
      if (data?.session) {
        const destination = getRoleDashboardPath(selectedRole);
        setTimeout(() => router.replace(destination), 1500);
      }
    } catch (err: any) {
      setError(err?.message || 'Sign up failed. Please try again.');
    }
    setLoading(false);
  };

  const pwStrength = getPasswordStrength(password);

  const nameError = touched.fullName && !fullName.trim() ? 'Full name is required' : '';
  const emailError = touched.email && email && !isValidEmail(email) ? 'Enter a valid email address' : '';
  const phoneError = touched.phone && phone && !isValidPhone(phone) ? 'Enter a valid phone number' : '';
  const pwError = touched.password && password && password.length < 8 ? 'At least 8 characters required' : '';
  const confirmError = touched.confirmPassword && confirmPassword && password !== confirmPassword ? 'Passwords do not match' : '';

  if (success) {
    return (
      <main
        className="min-h-screen flex items-center justify-center px-5 py-12"
        style={{ background: 'linear-gradient(135deg, #0B2348 0%, #0d1f3c 50%, #091a35 100%)' }}
      >
        <div
          className="text-center max-w-sm w-full rounded-3xl p-8"
          style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 32px 64px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
          }}
        >
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(244,197,66,0.2), rgba(244,197,66,0.05))',
              border: '2px solid #F4C542',
              boxShadow: '0 0 32px rgba(244,197,66,0.3)',
            }}
          >
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#F4C542" strokeWidth="2.5">
              <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="text-white font-bold text-2xl mb-2">Welcome to YatraNest!</h2>
          <p className="text-white/60 text-sm mb-5 leading-relaxed">
            Your account has been created. Check your email and click the confirmation link to activate your account.
          </p>
          <div
            className="p-4 rounded-2xl text-left mb-6"
            style={{ background: 'rgba(244,197,66,0.08)', border: '1px solid rgba(244,197,66,0.2)' }}
          >
            <p className="text-[#F4C542] text-xs font-semibold mb-1">📧 Check your inbox</p>
            <p className="text-white/50 text-xs">We sent a confirmation email to <span className="text-white font-medium">{email}</span>.</p>
          </div>
          <Link
            href="/login"
            className="inline-block w-full py-3.5 rounded-2xl text-sm font-bold text-[#0B2348] text-center transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg, #F4C542, #e6b830)', boxShadow: '0 8px 24px rgba(244,197,66,0.35)' }}
          >
            Go to Sign In
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen flex items-center justify-center px-5 py-10 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0B2348 0%, #0d1f3c 55%, #091a35 100%)' }}
    >
      {/* Ambient background orbs */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '-10%', left: '-5%', width: '50vw', height: '50vw',
          background: 'radial-gradient(circle, rgba(244,197,66,0.06) 0%, transparent 70%)',
          borderRadius: '50%',
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: '-10%', right: '-5%', width: '45vw', height: '45vw',
          background: 'radial-gradient(circle, rgba(56,189,248,0.05) 0%, transparent 70%)',
          borderRadius: '50%',
        }}
      />

      <div
        className="w-full max-w-md relative"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Logo */}
        <div className="text-center mb-7">
          <div className="flex justify-center mb-5">
            <BrandLogo size={44} showTagline={false} asLink={true} textColor="light" glow={true} />
          </div>
          <h1 className="text-white font-bold text-3xl mb-2 tracking-tight">Create your account</h1>
          <p className="text-white/50 text-sm">Start exploring Nepal and the world.</p>
        </div>

        {/* Animated Stepper */}
        <div className="flex items-center gap-0 mb-7 px-1">
          {[1, 2].map((s, idx) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2.5 flex-1">
                <div
                  className="relative flex items-center justify-center shrink-0 transition-all duration-500"
                  style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: s < step
                      ? 'linear-gradient(135deg, #F4C542, #e6b830)'
                      : s === step
                      ? 'rgba(244,197,66,0.12)'
                      : 'rgba(255,255,255,0.05)',
                    border: s === step
                      ? '2px solid #F4C542'
                      : s < step
                      ? '2px solid #F4C542' :'2px solid rgba(255,255,255,0.15)',
                    boxShadow: s === step
                      ? '0 0 16px rgba(244,197,66,0.35), 0 0 32px rgba(244,197,66,0.15)'
                      : s < step
                      ? '0 0 12px rgba(244,197,66,0.25)'
                      : 'none',
                  }}
                >
                  {s < step ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0B2348" strokeWidth="3">
                      <path d="M20 6L9 17l-5-5" strokeLinecap="round"/>
                    </svg>
                  ) : (
                    <span
                      className="text-xs font-bold"
                      style={{ color: s === step ? '#F4C542' : 'rgba(255,255,255,0.3)' }}
                    >
                      {s}
                    </span>
                  )}
                  {s === step && (
                    <div
                      className="absolute inset-0 rounded-full animate-ping"
                      style={{ background: 'rgba(244,197,66,0.2)', animationDuration: '2s' }}
                    />
                  )}
                </div>
                <div className="flex flex-col">
                  <span
                    className="text-xs font-semibold transition-colors duration-300"
                    style={{ color: s <= step ? '#F4C542' : 'rgba(255,255,255,0.3)' }}
                  >
                    {s === 1 ? 'Your Info' : 'Security'}
                  </span>
                  <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.25)' }}>
                    {s === 1 ? 'Role & details' : 'Password'}
                  </span>
                </div>
              </div>
              {idx < 1 && (
                <div
                  className="flex-1 h-px mx-2 transition-all duration-500"
                  style={{
                    background: step > 1
                      ? 'linear-gradient(90deg, #F4C542, rgba(244,197,66,0.3))'
                      : 'rgba(255,255,255,0.1)',
                    maxWidth: 40,
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Main Card */}
        <div
          className="rounded-3xl overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 32px 64px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.1)',
            opacity: animating ? 0 : 1,
            transform: animating ? 'translateX(-20px)' : 'translateX(0)',
            transition: 'opacity 0.25s ease, transform 0.25s ease',
          }}
        >
          <div className="p-6">
            {step === 1 ? (
              <form onSubmit={goToStep2} className="space-y-5">
                {/* Role Selection */}
                <div>
                  <p className="text-white/50 text-xs font-semibold uppercase tracking-widest mb-3">I am a...</p>
                  <div className="grid grid-cols-1 gap-2.5">
                    {ROLES.map((role, i) => {
                      const isSelected = selectedRole === role.value;
                      return (
                        <button
                          key={role.value}
                          type="button"
                          onClick={() => setSelectedRole(role.value)}
                          className="relative flex items-center gap-4 p-4 rounded-2xl text-left transition-all duration-300 group"
                          style={{
                            background: isSelected
                              ? `linear-gradient(135deg, ${role.gradient.replace('from-', '').replace(' to-', ', ')}, rgba(244,197,66,0.08))`
                              : 'rgba(255,255,255,0.04)',
                            border: isSelected
                              ? '1.5px solid #F4C542' :'1.5px solid rgba(255,255,255,0.08)',
                            boxShadow: isSelected
                              ? `0 0 24px rgba(244,197,66,0.18), 0 4px 16px rgba(0,0,0,0.2)`
                              : '0 2px 8px rgba(0,0,0,0.1)',
                            transform: isSelected ? 'scale(1.01)' : 'scale(1)',
                            animationDelay: `${i * 60}ms`,
                          }}
                        >
                          {/* Embroidered icon */}
                          <div
                            className="shrink-0 flex items-center justify-center rounded-2xl transition-all duration-300"
                            style={{
                              width: 52, height: 52,
                              background: isSelected
                                ? 'linear-gradient(135deg, rgba(244,197,66,0.2), rgba(244,197,66,0.05))'
                                : 'rgba(255,255,255,0.06)',
                              border: isSelected
                                ? '1.5px solid rgba(244,197,66,0.4)'
                                : '1.5px solid rgba(255,255,255,0.1)',
                              boxShadow: isSelected
                                ? `0 0 16px ${role.glow}, inset 0 1px 0 rgba(255,255,255,0.15)`
                                : 'inset 0 1px 0 rgba(255,255,255,0.08)',
                              fontSize: 24,
                            }}
                          >
                            {role.emoji}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div
                              className="font-bold text-[15px] mb-0.5 transition-colors duration-200"
                              style={{ color: isSelected ? '#F4C542' : 'rgba(255,255,255,0.9)' }}
                            >
                              {role.label}
                            </div>
                            <div className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                              {role.description}
                            </div>
                          </div>

                          {/* Animated checkmark */}
                          <div
                            className="shrink-0 flex items-center justify-center rounded-full transition-all duration-300"
                            style={{
                              width: 24, height: 24,
                              background: isSelected ? '#F4C542' : 'rgba(255,255,255,0.06)',
                              border: isSelected ? '2px solid #F4C542' : '2px solid rgba(255,255,255,0.15)',
                              transform: isSelected ? 'scale(1)' : 'scale(0.8)',
                              opacity: isSelected ? 1 : 0.4,
                              boxShadow: isSelected ? '0 0 12px rgba(244,197,66,0.4)' : 'none',
                            }}
                          >
                            <svg
                              width="12" height="12" viewBox="0 0 24 24" fill="none"
                              stroke={isSelected ? '#0B2348' : 'rgba(255,255,255,0.3)'}
                              strokeWidth="3"
                              style={{
                                transform: isSelected ? 'scale(1)' : 'scale(0)',
                                transition: 'transform 0.2s cubic-bezier(0.34,1.56,0.64,1)',
                              }}
                            >
                              <path d="M20 6L9 17l-5-5" strokeLinecap="round"/>
                            </svg>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
                  <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Your Details</span>
                  <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
                </div>

                {/* Full Name */}
                <FloatingInput
                  label="Full Name"
                  value={fullName}
                  onChange={(v) => { setFullName(v); setTouched(t => ({ ...t, fullName: true })); }}
                  required
                  autoComplete="name"
                  error={nameError}
                  success={touched.fullName && fullName.trim().length >= 2 && !nameError}
                />

                {/* Email */}
                <FloatingInput
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(v) => { setEmail(v); setTouched(t => ({ ...t, email: true })); }}
                  required
                  autoComplete="email"
                  error={emailError}
                  success={touched.email && isValidEmail(email)}
                />

                {/* Phone */}
                <FloatingInput
                  label="Phone Number"
                  type="tel"
                  value={phone}
                  onChange={(v) => { setPhone(v); setTouched(t => ({ ...t, phone: true })); }}
                  autoComplete="tel"
                  inputMode="tel"
                  error={phoneError}
                  success={touched.phone && phone.length > 0 && isValidPhone(phone)}
                />

                {error && (
                  <div
                    className="flex items-start gap-2.5 px-4 py-3 rounded-2xl text-sm"
                    style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" className="shrink-0 mt-0.5">
                      <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
                    </svg>
                    <span className="text-red-400">{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl font-bold text-[15px] text-[#0B2348] transition-all duration-200 hover:scale-[1.02] active:scale-[0.97] relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #F4C542 0%, #f0b429 50%, #e6a817 100%)',
                    boxShadow: '0 8px 32px rgba(244,197,66,0.4), 0 2px 8px rgba(0,0,0,0.2)',
                  }}
                >
                  <span className="relative z-10">Continue →</span>
                </button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* User summary pill */}
                <div
                  className="flex items-center gap-3 p-3.5 rounded-2xl"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
                    style={{
                      background: 'linear-gradient(135deg, rgba(244,197,66,0.2), rgba(244,197,66,0.05))',
                      border: '1px solid rgba(244,197,66,0.3)',
                    }}
                  >
                    {ROLES.find(r => r.value === selectedRole)?.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-semibold truncate">{fullName}</div>
                    <div className="text-white/40 text-xs truncate">{email} · {ROLES.find(r => r.value === selectedRole)?.label}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setStep(1); setError(''); }}
                    className="text-xs font-semibold px-3 py-1.5 rounded-xl transition-all duration-200 hover:scale-105"
                    style={{ color: '#F4C542', background: 'rgba(244,197,66,0.1)', border: '1px solid rgba(244,197,66,0.2)' }}
                  >
                    Edit
                  </button>
                </div>

                {/* Password */}
                <div>
                  <FloatingInput
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(v) => { setPassword(v); setTouched(t => ({ ...t, password: true })); }}
                    required
                    autoComplete="new-password"
                    error={pwError}
                    rightSlot={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="transition-colors duration-200"
                        style={{ color: 'rgba(255,255,255,0.4)' }}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" strokeLinecap="round"/>
                            <path d="M1 1l22 22" strokeLinecap="round"/>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        )}
                      </button>
                    }
                  />
                  {/* Password strength */}
                  {password.length > 0 && (
                    <div className="mt-2.5 space-y-1.5">
                      <div className="flex gap-1.5">
                        {[1, 2, 3, 4].map((i) => (
                          <div
                            key={i}
                            className="h-1 flex-1 rounded-full transition-all duration-400"
                            style={{
                              background: i <= pwStrength.score ? pwStrength.color : 'rgba(255,255,255,0.1)',
                              boxShadow: i <= pwStrength.score ? `0 0 6px ${pwStrength.color}60` : 'none',
                            }}
                          />
                        ))}
                      </div>
                      <p className="text-xs font-medium" style={{ color: pwStrength.color }}>
                        {pwStrength.label} password
                      </p>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <FloatingInput
                  label="Confirm Password"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(v) => { setConfirmPassword(v); setTouched(t => ({ ...t, confirmPassword: true })); }}
                  required
                  autoComplete="new-password"
                  error={confirmError}
                  success={touched.confirmPassword && confirmPassword.length > 0 && password === confirmPassword}
                  rightSlot={
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="transition-colors duration-200"
                      style={{ color: 'rgba(255,255,255,0.4)' }}
                      aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
                    >
                      {showConfirm ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" strokeLinecap="round"/>
                          <path d="M1 1l22 22" strokeLinecap="round"/>
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  }
                />

                {/* Terms */}
                <div
                  className="flex items-start gap-3 cursor-pointer select-none"
                  style={{ minHeight: 48 }}
                  onClick={() => { setAgreeTerms(v => !v); if (error === 'Please accept the terms and conditions.') setError(''); }}
                  role="checkbox"
                  aria-checked={agreeTerms}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      setAgreeTerms(v => !v);
                    }
                  }}
                >
                  <div
                    className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-all duration-200 pointer-events-none"
                    style={{
                      background: agreeTerms ? '#F4C542' : 'rgba(255,255,255,0.06)',
                      border: agreeTerms ? '2px solid #F4C542' : '2px solid rgba(255,255,255,0.2)',
                      boxShadow: agreeTerms ? '0 0 12px rgba(244,197,66,0.35)' : 'none',
                    }}
                    aria-hidden="true"
                  >
                    {agreeTerms && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0B2348" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5" strokeLinecap="round"/>
                      </svg>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => { setAgreeTerms(e.target.checked); }}
                    className="sr-only"
                    tabIndex={-1}
                  />
                  <span className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    I agree to the{' '}
                    <Link href="/terms" className="font-semibold hover:underline" style={{ color: '#F4C542' }} onClick={(e) => e.stopPropagation()}>
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link href="/privacy" className="font-semibold hover:underline" style={{ color: '#F4C542' }} onClick={(e) => e.stopPropagation()}>
                      Privacy Policy
                    </Link>
                  </span>
                </div>

                {error && (
                  <div
                    className="flex items-start gap-2.5 px-4 py-3 rounded-2xl text-sm"
                    style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" className="shrink-0 mt-0.5">
                      <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
                    </svg>
                    <span className="text-red-400">{error}</span>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setStep(1); setError(''); }}
                    className="px-5 py-4 rounded-2xl font-semibold text-sm transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]"
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: 'rgba(255,255,255,0.7)',
                    }}
                  >
                    ← Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !agreeTerms}
                    className="flex-1 py-4 rounded-2xl font-bold text-[15px] text-[#0B2348] transition-all duration-200 hover:scale-[1.02] active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    style={{
                      background: 'linear-gradient(135deg, #F4C542 0%, #f0b429 50%, #e6a817 100%)',
                      boxShadow: loading || !agreeTerms ? 'none' : '0 8px 32px rgba(244,197,66,0.4), 0 2px 8px rgba(0,0,0,0.2)',
                    }}
                  >
                    {loading ? (
                      <>
                        <div
                          className="w-4 h-4 rounded-full border-2 border-[#0B2348]/30 border-t-[#0B2348] animate-spin"
                        />
                        Creating account...
                      </>
                    ) : (
                      'Create Account ✓'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Footer links */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Already have an account?{' '}
            <Link
              href="/login"
              className="font-semibold hover:underline transition-colors"
              style={{ color: '#F4C542' }}
            >
              Sign In
            </Link>
          </p>
          <Link
            href="/home"
            className="text-xs block transition-colors hover:text-white/60"
            style={{ color: 'rgba(255,255,255,0.25)' }}
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </main>
  );
}
